import { useEffect, useMemo, useRef, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import {
  OrderStatus,
  OrderType,
} from "@/redux/services/order/order.type";
import { Roles } from "@/redux/services/user/user.type";

export type DateScope = "today" | "all";

export type TabValue = OrderStatus | null;

export type PaidFilter = "paid" | "unpaid" | null;

export interface RoleOrderConfig {
  visibleStatuses: TabValue[];
  defaultStatuses: OrderStatus[];
  defaultPaidFilter?: PaidFilter;
  lockPaidFilter?: boolean;
}

interface Args {
  limit: number;
  page: number;
  currentUserRoles: Roles[] | undefined;
  roleConfig: RoleOrderConfig;
  dateScope?: DateScope;
}

export function useOrderFilters({
  limit,
  page,
  currentUserRoles,
  roleConfig,
  dateScope = "all",
}: Args) {
  const [statusFilter, setStatusFilterState] = useState<TabValue>(null);
  const [tableFilter, setTableFilterState] = useState<string | null>(null);
  const [orderTypeFilter, setOrderTypeFilterState] =
    useState<OrderType | null>(null);
  const [paidFilter, setPaidFilterState] = useState<PaidFilter>(
    roleConfig.defaultPaidFilter ?? null
  );
  const [groupByTable, setGroupByTableState] = useState(false);
  const [searchOrderNumber, setSearchOrderNumberState] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const isFilterChange = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchOrderNumber);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchOrderNumber]);

  const markChanged = () => {
    isFilterChange.current = true;
  };
  const acknowledgeChange = () => {
    isFilterChange.current = false;
  };

  const setStatusFilter = (v: TabValue) => {
    markChanged();
    setStatusFilterState(v);
  };
  const setTableFilter = (v: string | null) => {
    markChanged();
    setTableFilterState(v);
  };
  const setOrderTypeFilter = (v: OrderType | null) => {
    markChanged();
    setOrderTypeFilterState(v);
  };
  const setPaidFilter = (v: PaidFilter) => {
    if (roleConfig.lockPaidFilter) return;
    markChanged();
    setPaidFilterState(v);
  };
  const setGroupByTable = (v: boolean) => {
    setGroupByTableState(v);
  };
  const setSearchOrderNumber = (v: string) => {
    markChanged();
    setSearchOrderNumberState(v);
  };

  const canTogglePaid =
    !currentUserRoles?.includes(Roles.kitchen) &&
    !roleConfig.lockPaidFilter;

  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = { limit, page };

    if (debouncedSearch !== "") {
      params.orderNumber = debouncedSearch;
      return params;
    }

    if (statusFilter) {
      params.status = statusFilter;
    } else if (roleConfig.defaultStatuses.length > 0) {
      params.status = roleConfig.defaultStatuses;
    }

    if (paidFilter === "paid") params.isPaid = true;
    else if (paidFilter === "unpaid") params.isPaid = false;

    if (tableFilter) params.table = tableFilter;
    if (orderTypeFilter) params.orderType = orderTypeFilter;

    if (dateScope === "today") {
      const now = new Date();
      params["createdAt[after]"] = startOfDay(now).toISOString();
      params["createdAt[before]"] = endOfDay(now).toISOString();
    }

    return params;
  }, [
    limit,
    page,
    debouncedSearch,
    statusFilter,
    paidFilter,
    tableFilter,
    orderTypeFilter,
    roleConfig,
    dateScope,
  ]);

  return {
    statusFilter,
    tableFilter,
    orderTypeFilter,
    paidFilter,
    groupByTable,
    searchOrderNumber,
    setStatusFilter,
    setTableFilter,
    setOrderTypeFilter,
    setPaidFilter,
    setGroupByTable,
    setSearchOrderNumber,
    queryParams,
    canTogglePaid,
    isFilterChange,
    acknowledgeChange,
  };
}
